import { useState, useMemo } from 'react';
import {
  Plus,
  Loader2,
  DollarSign,
  Pencil,
  Trash2,
  Star,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { AccessDenied } from '@/components/ui/AccessDenied';
import { UserRole } from '@pms/shared';
import { useAuth } from '@/hooks/useAuth';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import {
  useRatePlans,
  useCreateRatePlan,
  useUpdateRatePlan,
  useDeleteRatePlan,
} from '@/hooks/api/useRevenue';
import type {
  RatePlan,
  CreateRatePlanPayload,
  UpdateRatePlanPayload,
} from '@/services/api/revenue.api';

// ─── Page ────────────────────────────────────────────────────────────────────

export function RatePlansPage() {
  const { user } = useAuth();
  const canAccess = user?.role === UserRole.ADMIN || user?.role === UserRole.REVENUE_MANAGER;
  const propertyId = useSelectedPropertyId();
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<RatePlan | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: ratePlans, isLoading } = useRatePlans({ propertyId }, canAccess);
  const deleteMutation = useDeleteRatePlan();
  const updateMutation = useUpdateRatePlan();

  const allPlans: RatePlan[] = ratePlans?.data ?? [];

  const plans: RatePlan[] = useMemo(() => {
    if (!searchTerm) return allPlans;
    const q = searchTerm.toLowerCase();
    return allPlans.filter(
      (p) => p.name.toLowerCase().includes(q) || p.code.toLowerCase().includes(q),
    );
  }, [allPlans, searchTerm]);

  const handleToggleActive = (plan: RatePlan) => {
    updateMutation.mutate({ id: plan.id, payload: { isActive: !plan.isActive } });
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this rate plan?')) {
      deleteMutation.mutate(id);
    }
  };

  if (!canAccess) {
    return <AccessDenied message="Rate plan management is restricted to Admins and Revenue Managers." />;
  }

  return (
    <div>
      <PageHeader
        title="Rate Plans"
        description="Manage rate plans for your properties"
        actions={
          <Button onClick={() => { setEditingPlan(null); setShowForm(true); }}>
            <Plus size={16} aria-hidden="true" />
            Add Rate Plan
          </Button>
        }
      />

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name or code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
          aria-label="Search rate plans"
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : plans.length === 0 ? (
        <EmptyState
          icon={DollarSign}
          title="No rate plans"
          description="Create your first rate plan to get started."
          actionLabel="Add Rate Plan"
          onAction={() => setShowForm(true)}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Code</Th>
                  <Th>Default</Th>
                  <Th>Status</Th>
                  <Th>Description</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {plans.map((plan) => (
                  <tr key={plan.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{plan.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">{plan.code}</td>
                    <td className="px-4 py-3">
                      {plan.isDefault && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-700">
                          <Star size={12} aria-hidden="true" />
                          Default
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => handleToggleActive(plan)}
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium cursor-pointer',
                          plan.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500',
                        )}
                        aria-label={plan.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {plan.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate">
                      {plan.description ?? '--'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => { setEditingPlan(plan); setShowForm(true); }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                          aria-label="Edit rate plan"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(plan.id)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          aria-label="Delete rate plan"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SlideOver
        isOpen={showForm}
        onClose={() => { setShowForm(false); setEditingPlan(null); }}
        title={editingPlan ? 'Edit Rate Plan' : 'Add Rate Plan'}
      >
        <RatePlanForm
          propertyId={propertyId}
          editing={editingPlan}
          onClose={() => { setShowForm(false); setEditingPlan(null); }}
        />
      </SlideOver>
    </div>
  );
}

// ─── Form ────────────────────────────────────────────────────────────────────

function RatePlanForm({
  propertyId,
  editing,
  onClose,
}: {
  propertyId: string;
  editing: RatePlan | null;
  onClose: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [code, setCode] = useState(editing?.code ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [isDefault, setIsDefault] = useState(editing?.isDefault ?? false);

  const createMutation = useCreateRatePlan();
  const updateMutation = useUpdateRatePlan();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editing) {
      const payload: UpdateRatePlanPayload = { name, code, description: description || undefined, isDefault };
      updateMutation.mutate({ id: editing.id, payload }, { onSuccess: () => onClose() });
    } else {
      const payload: CreateRatePlanPayload = { propertyId, name, code, description: description || undefined, isDefault };
      createMutation.mutate(payload, { onSuccess: () => onClose() });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="rp-name" className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
        <input id="rp-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div>
        <label htmlFor="rp-code" className="block text-sm font-medium text-gray-700 mb-1">Code <span className="text-red-500">*</span></label>
        <input id="rp-code" type="text" required maxLength={10} value={code} onChange={(e) => setCode(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div>
        <label htmlFor="rp-desc" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea id="rp-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      <div className="flex items-center gap-2">
        <input id="rp-default" type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
        <label htmlFor="rp-default" className="text-sm font-medium text-gray-700">Set as default</label>
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={isPending}>{editing ? 'Update' : 'Create'}</Button>
      </div>
    </form>
  );
}

// ─── Table helper ────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}
